import type { GoogleSignInError } from '@sbc/domain-models/admin';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useGoogleSignInStatus } from '@/lib/api/google-sign-in';
import { useAuth } from '@/lib/auth/auth-context';
import { googleSignInHref, googleSignInMessage } from '@/lib/auth/google-sign-in';

export function LoginPage({
  redirect,
  googleError,
}: {
  redirect?: string;
  googleError?: GoogleSignInError;
}) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(
    googleError ? googleSignInMessage(googleError) : null,
  );
  const googleStatus = useGoogleSignInStatus();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      if (redirect) {
        // A raw path rather than a typed route: the return-to is whatever page
        // bounced here, already narrowed to this origin by the route's validateSearch.
        router.history.push(redirect);
      } else {
        void navigate({ to: '/' });
      }
    } catch {
      setError('Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center p-8">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>SBC Agent Admin · control plane</CardDescription>
        </CardHeader>
        <CardContent>
          {googleStatus.data?.enabled && (
            <>
              <Button asChild variant="outline" className="w-full">
                <a href={googleSignInHref(redirect)}>Sign in with Google</a>
              </Button>
              <div className="my-4 flex items-center gap-3 text-xs text-[var(--color-muted-foreground)]">
                <span className="h-px flex-1 bg-[var(--color-border)]" />
                or
                <span className="h-px flex-1 bg-[var(--color-border)]" />
              </div>
            </>
          )}

          <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-4 text-sm text-[var(--color-muted-foreground)]">
            <Link to="/forgot-password" className="underline">
              Forgot your password?
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
